/**
 * Row identity for a photo scanner's result list. Both scanners use it: the
 * meal-photo scanner (`pages/meal-photo-scanner-page`) and the recipe scanner
 * (`pages/recipe-scanner-page`).
 *
 * A scanner shows one row per food the photo model reported. Those rows are NOT
 * distinguishable by the food's name: the model returns a row per food it sees and
 * nothing on either side merges them, so one plate holding two of the same thing --
 * two fried eggs, two sandwiches whose bread decomposes into the same component --
 * produces two rows called the same name. A recipe does it just as readily, listing
 * olive oil once for the pan and again for the dressing. The scan prompts ask for
 * every distinct food item and for composite dishes to be broken into components, so
 * this is ordinary rather than exotic.
 *
 * Both pages used the name as a key anyway, and `Array.prototype.find` returns the
 * FIRST match, so adjusting the weight of the second egg wrote that weight onto the
 * first one and left the tapped row with its old estimate. Nothing reported it.
 *
 * `mealIngredientsBesidesTheAdjustedRow`, in the meal-photo scanner's folder, had the
 * same mistake and was fixed by comparing the row OBJECTS. That works there because
 * the row is in hand inside the page. It cannot work for the lookups this module
 * serves, because their identifier crosses a page navigation: it travels in
 * `returnContext`, inside `local`, which `nav-controller.ts` serialises into
 * `sessionStorage`. An object reference does not survive being written out and read
 * back. A string does.
 *
 * Kept as plain functions apart from the pages, for the same reason
 * `pages/meal-photo-scanner-page/meal-scan-running-total.ts` is: the rule can then be
 * unit-tested without standing up a whole Angular page. It sits here beside the other
 * shared scanner modules -- `scan-config.ts`, `scanned-food-match.ts`, `scan-progress.ts`,
 * `scanner-preference-memory.ts` -- rather than inside either page, because a page
 * importing out of the other page's folder would read as a mistake.
 */

/** A row of a scanner's result list, reduced to the one field this module reads. */
export interface ScannedRowIdentity {
  /**
   * Identifies this row for as long as the app is running. Minted when the row is
   * built and never derived from anything the row shows, so two rows for the same
   * food still get different ones.
   */
  rowId: string;
}

// counts every scanned row this browser session has built, so no two ids collide.
// Module-level rather than per-page on purpose: the ids only ever have to be
// different from each other, and a fresh counter per page instance would hand a
// second scan the same ids the first scan's still-open child page is carrying.
let scannedRowsMintedSoFar = 0;

/**
 * A fresh row id, different from every id minted before it in this browser session.
 *
 * The number in it means nothing -- do not read a row's position, age or anything
 * else out of an id. It exists to be compared for equality and nothing more.
 */
export function mintScannedRowId(): string {
  scannedRowsMintedSoFar++;
  return `scanned-row-${scannedRowsMintedSoFar}`;
}

/**
 * The row carrying this id, or `undefined` when the list no longer holds it.
 *
 * Undefined is a real answer rather than a fault: a child page can come back to a
 * result list that has since been thrown away and rebuilt by a retake, and its id
 * then belongs to no row. That is exactly the case an id gets right and a row's
 * position in the list gets wrong -- a position would silently address whatever row
 * now sits there.
 */
export function findScannedRowById<T extends ScannedRowIdentity>(rows: T[], rowId: string): T | undefined {
  return rows.find(row => row.rowId === rowId);
}
